/**
 * cart-add.js
 * カートボタン・ラッピングモーダルの統合管理
 *
 * グローバル依存:
 *   CART_HAS_FROZEN_ONLY / CART_HAS_NORMAL_ONLY  - assets_cart_btn.liquid が出力
 *   close_product_modal()                         - application.js が定義
 *
 * グローバル公開:
 *   window.close_modal_box(ele)  - product_bar_classic.liquid の back_btn から呼ばれる
 */
(() => {
  // ============================================
  // 温度帯チェック
  // ============================================

  /**
   * 配送条件がカート内の商品と競合するか確認する
   * @param {string} shippingCondition - カンマ区切りの配送条件 (例: "冷凍", "常温", "冷凍,常温")
   * @returns {boolean} 競合する場合 true
   */
  const checkTemperatureConflict = (shippingCondition) => {
    const conditions = (shippingCondition || '').split(',');
    const isFrozenOnly = conditions.includes('冷凍') && !conditions.includes('常温');
    const isNormalOnly = conditions.includes('常温') && !conditions.includes('冷凍');

    // CART_HAS_FROZEN_ONLY / CART_HAS_NORMAL_ONLY は assets_cart_btn.liquid が出力するグローバル変数
    const cartHasFrozenOnly = typeof CART_HAS_FROZEN_ONLY !== 'undefined' && CART_HAS_FROZEN_ONLY;
    const cartHasNormalOnly = typeof CART_HAS_NORMAL_ONLY !== 'undefined' && CART_HAS_NORMAL_ONLY;

    return (isFrozenOnly && cartHasNormalOnly) || (isNormalOnly && cartHasFrozenOnly);
  };

  // ============================================
  // カート追加処理
  // ============================================

  /**
   * Shopify Cart AJAX API でカートに商品を追加する
   * @param {Array<{id: string|number, quantity: number, properties?: Object}>} items
   * @returns {Promise<Object>} 追加後のカートデータ
   */
  const addItemsToCart = async (items) => {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.description || 'カートへの追加に失敗しました。');
    }
    return response.json();
  };

  // ============================================
  // ラッピングモーダル
  // ============================================

  /**
   * .box_select ボタンに対応する .box_option 要素を DOM から探す
   * 複数の HTML 構造パターンに対応（DOM 構造の違いを吸収）
   * 一覧ページでは別商品の .box_option を掴む恐れがあるため、
   * ボタンの data-product-id と .box_option の variant-id（実体は product.id）が
   * 一致する要素だけを採用する。一致するものがなければ null を返す。
   * @param {Element} btn - .box_select ボタン要素
   * @returns {Element|null}
   */
  const findBoxOption = (btn) => {
    const productId = btn.dataset.productId;
    if (!productId) return null;

    const isMatched = (el) => el.classList.contains('box_option') && el.getAttribute('variant-id') === productId;

    const findInChildren = (parent, exclude) =>
      [...parent.children].find(el => el !== exclude && isMatched(el)) ?? null;

    // パターン1: ボタンの直接の兄弟
    const fromSibling = findInChildren(btn.parentElement, btn);
    if (fromSibling) return fromSibling;

    // パターン2: ボタンの親の兄弟
    const parent = btn.parentElement;
    if (parent.parentElement) {
      const fromParentSibling = findInChildren(parent.parentElement, parent);
      if (fromParentSibling) return fromParentSibling;
    }

    // パターン3: 祖先コンテナ内を検索
    const ancestor = btn.closest('.cart-button-wrapper, .product-item') ?? btn.closest('div');
    return [...(ancestor?.querySelectorAll('.box_option') ?? [])].find(isMatched) ?? null;
  };

  /**
   * ラッピング選択モーダルを開く
   * @param {Element} btn - .box_select ボタン要素
   */
  const showModalBox = (btn) => {
    const content = document.querySelector('.modal_box_content');
    content.innerHTML = '';

    const boxOption = findBoxOption(btn);
    if (!boxOption) {
      return;
    }

    const clone = boxOption.cloneNode(true);
    clone.style.display = '';
    content.appendChild(clone);

    new Swiper('.modal_box_content .swiper-container', {
      speed: 600,
      watchOverflow: true,
      slidesPerView: 2.3,
      spaceBetween: 20,
      observer: true,
      slideToClickedSlide: false,
      breakpoints: {
        767: { slidesPerView: 2.5, slidesOffsetBefore: 10, slidesOffsetAfter: 10 },
        768: { slidesPerView: 3, spaceBetween: 40, slidesOffsetBefore: 0, slidesOffsetAfter: 120 },
      },
    });

    document.querySelector('.modal_box').classList.add('open');
    document.body.style.overflow = 'hidden';
  };

  /**
   * ラッピング選択モーダルを閉じる
   * product_bar_classic.liquid の back_btn からも呼ばれるため window.close_modal_box として公開する
   * @param {Element} closeBtn - .close_btn または .back_btn 要素
   */
  const closeModalBox = (closeBtn) => {
    const modal = document.querySelector('.modal_box');
    modal.classList.remove('open');

    const cleanup = () => {
      modal.querySelectorAll('.box_option').forEach(el => { el.style.display = 'none'; });
      document.querySelectorAll('.box_select').forEach(el => el.classList.remove('on'));
      modal.querySelectorAll('.back_btn').forEach(el => { el.style.display = 'none'; });
    };

    // closeBtn の兄弟に variant-id 付き .box_option がある場合は 800ms 後にクリーンアップ
    const siblingBoxOption = [...closeBtn.parentElement.children]
      .find(el => el !== closeBtn && el.classList.contains('box_option'));

    if (siblingBoxOption?.hasAttribute('variant-id')) {
      setTimeout(cleanup, 800);
    } else {
      cleanup();
    }

    document.body.style.overflow = 'visible';
  };

  // product_bar_classic.liquid から close_modal_box(this) として参照されるため公開
  window.close_modal_box = closeModalBox;

  // ============================================
  // イベントハンドラー
  // ============================================

  /**
   * .js-cart-add-btn クリック（オプションなしでカートに追加）
   */
  const handleCartAddClick = async (event) => {
    const button = event.target.closest('.js-cart-add-btn');
    if (!button) return;

    event.preventDefault();

    const { variantId, shippingCondition = '' } = button.dataset;
    if (!variantId) {
      return;
    }

    if (checkTemperatureConflict(shippingCondition)) {
      alert('冷凍配送・常温配送の商品は同時に購入できません。\nカートから商品を削除してから再度お試しください。');
      return;
    }

    button.disabled = true;
    try {
      await addItemsToCart([{ id: variantId, quantity: 1 }]);
      location.href = '/cart';
    } catch (error) {
      alert(error.message || 'カートへの追加に失敗しました。');
      button.disabled = false;
    }
  };

  /**
   * .box_select クリック（温度帯チェック後にラッピングモーダルを開く）
   */
  const handleBoxSelectClick = (event) => {
    const button = event.target.closest('.box_select');
    if (!button) return;

    const { shippingCondition = '' } = button.dataset;
    if (checkTemperatureConflict(shippingCondition)) {
      alert('冷凍配送・常温配送の商品は同時に購入できません。\nカートから商品を削除してから再度お試しください。');
      return;
    }

    if (typeof close_product_modal === 'function') close_product_modal();
    showModalBox(button);
  };

  /**
   * .with_wrapping_btn クリック（ラッピング・のしと本商品をカートに追加）
   */
  const handleWrappingBtnClick = async (event) => {
    const btn = event.target.closest('.with_wrapping_btn');
    if (!btn) return;

    const { parent: parentId, id: boxId, sku: parentSku, label: parentLabel } = btn.dataset;
    const properties = { _parentSKU: parentSku };
    if (parentLabel) properties._parentLabel = parentLabel;

    try {
      await addItemsToCart([
        { id: parentId, quantity: 1 },
        { id: boxId, quantity: 1, properties },
      ]);
      window.location = '/cart';
    } catch {
      alert('在庫が足りませんでした。');
    }
  };

  /**
   * .modal_box .close_btn クリック（モーダルを閉じる）
   */
  const handleModalCloseBtnClick = (event) => {
    const closeBtn = event.target.closest('.modal_box .close_btn');
    if (!closeBtn) return;
    closeModalBox(closeBtn);
  };

  /**
   * .btn_option クリック（ダブルクリック防止）
   */
  const handleBtnOptionClick = (event) => {
    const btn = event.target.closest('.btn_option');
    if (!btn) return;
    btn.style.pointerEvents = 'none';
  };

  // ============================================
  // 初期化（defer 読み込みのため DOM は確実に準備済み）
  // ============================================
  document.addEventListener('click', handleCartAddClick);
  document.addEventListener('click', handleBoxSelectClick);
  document.addEventListener('click', handleWrappingBtnClick);
  document.addEventListener('click', handleModalCloseBtnClick);
  document.addEventListener('click', handleBtnOptionClick);
})();
